/**
 * Brands (SEC-334 white-label)
 *
 * Securdia is the built-in default brand and means "today's look": the existing
 * country based code paths (constants/app.js, siteContext.js) keep handling it.
 *
 * Partner brands (CSG and any later partner) are created by admins on the
 * reseller's Branding and stored on the reseller. The API tells the portal which brand
 * belongs to the web address it is loaded on (GET /brand/current), and
 * brandFromRecord() turns that record into the shape components use as this.$brand.
 */

export const DEFAULT_BRAND_KEY = "securdia";

// Today's colours, used to prefill the Brands form so nothing is empty
export const SECURDIA_COLORS = {
  primary: {
    50: "#EBF5FD",
    100: "#D4EAFB",
    200: "#A6D2F6",
    300: "#77BBF2",
    400: "#49A4ED",
    500: "#1A8DE9",
    600: "#1272BE",
    700: "#1C497C",
    800: "#153a63",
    900: "#0e2a47",
  },
  accent: "#F29100",
  overlay: "rgba(26, 141, 233, 0.38)",
};

export const SECURDIA_TYPOGRAPHY = {
  headingFont: "Source Serif Pro",
  textFont: "Source Sans Pro",
  stylesheetUrl: "",
};

// Every brand object has the same keys, so a loaded record can replace the
// default in place (Vue reactivity only tracks keys that exist from the start).
export const SECURDIA = {
  key: "securdia",
  name: "Securdia",
  isDefault: true,
  productName: "SecurCloud",
  hosts: [],
  portalUrl: "",
  analyticsId: "G-FJ12VS5WYV", // Securdia's own Google Analytics property
  favicon: `${process.env.BASE_URL}favicon.png`,
  logos: {
    horizontal: require("@/assets/logos/logo_horizontal.png"),
    menu: require("@/assets/logos/logo_menu.png"),
    pdf: require("@/assets/images/securdia_pdf_logo.png"),
    icon: require("@/assets/logos/favicon.png"),
  },
  colors: null,     // Securdia colours stay in the stylesheets
  typography: null, // Securdia fonts stay in the stylesheets
  contact: null,    // Securdia contact details stay in constants/app.js
  website: null,
};

/**
 * The API hands out its logos as paths, not as storage addresses, so that a rendered
 * partner page never carries the storage host. Turn such a path into a full address.
 * A value that is already a full address (a logo typed in by hand) is left alone.
 */
function logoUrl(value, fallback) {
  if (!value) return fallback;
  if (/^https?:\/\//.test(value)) return value;
  return `${process.env.VUE_APP_SERVER_URL}${value}`;
}

/**
 * A stand-in drawn from the brand's own name, for a brand that has not uploaded a logo
 * yet. It is a PNG data address, so it works the same in an <img>, as the tab icon and
 * inside a generated PDF, and it costs no request. Built once per brand and name.
 *
 * This exists so that a half-configured partner brand looks unfinished rather than
 * looking like Securdia: a partner page must never show a Securdia file (SEC-334).
 *
 * @param {string} name  the brand name, e.g. "CSG"
 * @param {string} shade "dark" for light backgrounds, "light" for the dark sidebar
 */
const placeholderCache = {};

function namePlaceholder(name, shade) {
  const text = String(name || "").trim() || "Brand";
  const cacheKey = `${shade}:${text}`;
  if (placeholderCache[cacheKey]) return placeholderCache[cacheKey];

  const HEIGHT = 80;
  const FONT = "bold 34px Arial, Helvetica, sans-serif";

  const canvas = document.createElement("canvas");
  const measure = canvas.getContext("2d");
  measure.font = FONT;

  canvas.width = Math.ceil(measure.measureText(text).width) + 24; // a little air on both sides
  canvas.height = HEIGHT;

  // the context resets when the canvas is resized, so set the font again
  const ctx = canvas.getContext("2d");
  ctx.font = FONT;
  ctx.textBaseline = "middle";
  ctx.fillStyle = shade === "light" ? "#ffffff" : "#25292c";
  ctx.fillText(text, 12, HEIGHT / 2);

  placeholderCache[cacheKey] = canvas.toDataURL("image/png");
  return placeholderCache[cacheKey];
}

// Only the keys that hold a value, so a stored "" cannot override a default
function withoutEmpty(object) {
  const kept = {};
  for (const [key, value] of Object.entries(object || {})) {
    if (value !== "" && value !== null && value !== undefined) kept[key] = value;
  }
  return kept;
}

/**
 * Turn a brand record from the API into the shape components use.
 *
 * A missing logo never falls back to a Securdia file. It falls back to the brand's own
 * horizontal logo where a dark-on-light mark fits (PDF, mail, tab icon), and to a
 * placeholder built from the brand name where it does not: the sidebar is dark, so a
 * dark logo there would be invisible.
 */
export function brandFromRecord(record) {
  const logos = record.logos || {};
  const contact = record.contact || {};

  // the brand's main mark, or its name if it has none
  const horizontal = logoUrl(logos.horizontal, "") || namePlaceholder(record.name, "dark");
  const menu = logoUrl(logos.menu, "") || namePlaceholder(record.name, "light");

  return {
    key: record.key,
    name: record.name,
    isDefault: false,
    productName: record.productName || "SecurCloud",
    hosts: record.hosts || [],
    portalUrl: record.portalUrl || "",
    analyticsId: record.analyticsId || "", // empty = this brand gets no analytics at all
    portalLanguage: record.portalLanguage || "", // SEC-334: language the portal starts in; empty = the country rule
    favicon: logoUrl(logos.favicon, "") || horizontal,
    logos: {
      horizontal,                                      // dark logo for light backgrounds
      menu,                                            // light logo for the dark sidebar
      pdf: logoUrl(logos.pdf, "") || horizontal,
      icon: logoUrl(logos.favicon, "") || menu,        // sidebar footer icon, on the dark menu
    },
    // stored fields can be "" until an admin fills them in; an empty shade must fall back
    // to the Securdia value, not become an invalid CSS colour
    colors: {
      primary: { ...SECURDIA_COLORS.primary, ...withoutEmpty((record.colors || {}).primary) },
      accent: (record.colors || {}).accent || SECURDIA_COLORS.accent,
      overlay: (record.colors || {}).overlay || SECURDIA_COLORS.overlay,
    },
    typography: { ...SECURDIA_TYPOGRAPHY, ...withoutEmpty(record.typography) },
    // Same field names as constants/app.js, so the PDF and the sidebar can use either
    contact: {
      company_name: contact.companyName || record.name,
      company_number: contact.companyNumber || "",
      company_address: contact.address || "",
      email: contact.supportEmail || "",
      phone: contact.supportPhone || "",
      contact_person: `${record.name} support`,
      contact_email: contact.supportEmail || "",
      contact_phone: contact.supportPhone || "",
    },
    website: {
      url: contact.websiteUrl || "",
      label: contact.websiteLabel || String(contact.websiteUrl || "").replace(/^https?:\/\//, ""),
    },
  };
}
